import { useWeb3React } from '@web3-react/core'
import { Web3Provider } from '@ethersproject/providers'
import { useCallback } from 'react'
import { L1ChainId } from '../../types'
import { useChainType } from '../../store/connect/hooks'
import { ChainType } from '../../constants/chains'
import toastify from '../../components/Toastify'
import { NetworkRegistry } from '../../utils/networkRegistry'
import { AddEthereumChainParameter, Connector } from '@web3-react/types'
import { didUserReject } from '../../connection/utils'
import { getConnection } from '../../connection'

async function trySwitchNetwork(
  connector: Connector,
  desiredChainIdOrChainParameters: number | AddEthereumChainParameter,
) {
  try {
    await connector.activate(desiredChainIdOrChainParameters)
  } catch (e) {
    const connection = getConnection(connector)
    if (connection && didUserReject(connection, e)) {
      return
    } else {
      console.error(e)
      toastify.error(e?.message)
    }
  }
}

export function useSwitchNetwork() {
  const { connector } = useWeb3React<Web3Provider>()
  const chainType = useChainType()
  return useCallback(
    async (chainId: L1ChainId) => {
      const network = NetworkRegistry.byL1(chainId)
      if (network?.chainType !== chainType) {
        if (chainType === ChainType.Evm)
          toastify.warn(
            `Disconnect the current wallet and connect using the Starknet wallet.`,
          )
        else if (chainType === ChainType.Starknet)
          toastify.warn(
            `Disconnect the current wallet and connect using the Metamask wallet.`,
          )

        return
      }
      // Try to add the network to the user's wallet
      if (network) {
        const chainParameters: AddEthereumChainParameter = {
          chainId: Number(chainId),
          chainName: network.name!,
          nativeCurrency: {
            name: network.symbol!,
            symbol: network.symbol!,
            decimals: network.decimals as 18,
          },
          rpcUrls: [network.rpcUrl!],
          blockExplorerUrls: [network.explorerUrl!],
        }

        await trySwitchNetwork(connector, chainParameters)
      } else {
        // chainId is a hex string, need to convert to number
        await trySwitchNetwork(connector, Number(chainId))
      }
    },
    [connector, chainType],
  )
}
